export const APPLY_URL = 'https://form.typeform.com/to/lnxghagC';
export const DISCORD_URL = 'https://discord.gg/gt56cQ4nWb';
export const SUPPORT_EMAIL = 'support@teamplay.gg';

export type RoleCategory = 'community' | 'smm' | 'hosts' | 'open';

export type RoleDetailBlock = {
  label: string;
  text?: string;
  list?: string[];
};

export type Role = {
  id: RoleCategory;
  track: string;
  title: string;
  summary: string;
  tags: string[];
  snippet: string[];
  detailBlocks: RoleDetailBlock[];
  note?: string;
  applyAction: string;
};

export type RoleFilter = {
  id: RoleCategory | 'all';
  label: string;
};

export const ROLE_FILTERS: RoleFilter[] = [
  { id: 'all', label: 'All' },
  { id: 'community', label: 'Community' },
  { id: 'smm', label: 'SMM' },
  { id: 'hosts', label: 'Session Hosts' },
  { id: 'open', label: 'Open Track' },
];

export const ROLES: Role[] = [
  {
    id: 'community',
    track: 'Community',
    title: 'Community Manager (Discord / Reddit)',
    summary: 'Keep our Discord alive – events, sessions, and conversations people come back for.',
    tags: ['Remote', 'Flexible hours', 'Community-first', 'Long-term potential'],
    snippet: [
      'Run events and LFG sessions',
      'Turn feedback into product ideas',
      'Keep the vibe good and low-tox',
    ],
    detailBlocks: [
      {
        label: 'What you would do',
        list: [
          'Host regular sessions and small events.',
          'Keep discussions healthy and welcoming.',
          'Build formats people look forward to.',
        ],
      },
      {
        label: 'You would fit if',
        list: [
          'You know what makes a Discord stick.',
          'You stay calm and clear with people.',
          'You care about the community, not just the numbers.',
        ],
      },
    ],
    applyAction: 'apply_community',
  },
  {
    id: 'smm',
    track: 'Marketing',
    title: 'SMM Manager (X / TikTok / YouTube / Instagram)',
    summary: 'Grow our socials with content that actually feels native to gamers.',
    tags: ['Remote', 'Flexible', 'Content-driven', 'Grow with the brand'],
    snippet: [
      'Make short-form content',
      'Turn updates into posts people share',
      'Track what works and do more of it',
    ],
    detailBlocks: [
      {
        label: 'What you would do',
        list: [
          'Own the content plan and posting rhythm.',
          'Make clips, memes, and posts.',
          'Turn community moments into growth.',
        ],
      },
      {
        label: 'You would fit if',
        list: [
          'You can write for gamers.',
          'You get how each platform works.',
          'You move fast from idea to post.',
        ],
      },
    ],
    applyAction: 'apply_smm',
  },
  {
    id: 'hosts',
    track: 'Sessions',
    title: 'Session Hosts (Creators / Companions / Pros)',
    summary: 'Run live sessions where players join through Teamplay and actually have a good time.',
    tags: ['Remote', 'Your own schedule', 'Build your audience', 'Creator-friendly'],
    snippet: [
      'Run lobbies, coaching, or social sessions',
      'Set the vibe and the rules',
      'Grow a crowd around your style',
    ],
    detailBlocks: [
      {
        label: 'How it works',
        text: 'You open a session with a few slots. Players join through Teamplay, hop in voice, and play toward a clear goal together.',
      },
      {
        label: 'You would fit if',
        list: [
          'You are comfortable leading a voice lobby.',
          'You balance fun and structure.',
          'You show up when you say you will.',
        ],
      },
    ],
    applyAction: 'apply_hosts',
  },
  {
    id: 'open',
    track: 'Open Collaboration',
    title: 'Open Track (Partnerships / Growth)',
    summary: 'Got a sharp idea to grow Teamplay? Pitch it. Operators, marketers, creators – all welcome.',
    tags: ['Remote', 'Flexible scope', 'Pitch your idea', 'Outcome-driven'],
    snippet: [
      'Partnerships with communities and brands',
      'Growth experiments',
      'Collabs with creators',
    ],
    detailBlocks: [
      {
        label: 'What to send',
        list: [
          'What you want to tackle.',
          'How you would go about it.',
          'What you would need from us.',
        ],
      },
    ],
    applyAction: 'apply_open',
  },
];

export type ValuePoint = { title: string; description: string };

export const VALUE_POINTS: ValuePoint[] = [
  {
    title: 'Same mission',
    description: 'We all want one thing: an easy way to find good people to play with.',
  },
  {
    title: 'Real ownership',
    description: 'You run real pieces of the product and shape where they go.',
  },
  {
    title: 'Small, tight team',
    description: 'No bureaucracy. Just people who ship and care.',
  },
  {
    title: 'Grow with it',
    description: 'As Teamplay grows, the people who built it grow with it.',
  },
];

export const QUICK_FACTS: string[] = [
  'Remote and flexible',
  'Gamers building for gamers',
  'Real ownership from day one',
  'In it for the long game',
];

export type ProcessStep = { step: number; title: string; description: string };

export const PROCESS_STEPS: ProcessStep[] = [
  {
    step: 1,
    title: 'Say hi',
    description: 'Fill the short form or ping us on Discord. Tell us what you would want to work on.',
  },
  {
    step: 2,
    title: 'Quick chat',
    description: 'A short, casual call to see if we click and where you fit.',
  },
  {
    step: 3,
    title: 'Start building',
    description: 'Jump into a real piece of the product and see how it feels.',
  },
];

export type FaqEntry = { question: string; answer: string };

export const CAREERS_FAQ: FaqEntry[] = [
  {
    question: 'Do I need games-industry experience?',
    answer:
      'Not really. We care more that you get gamers and can execute. Show us something you have made.',
  },
  {
    question: 'Can I do more than one thing?',
    answer:
      'Yes. Tell us everything you are into – roles here are flexible and we shape them around people.',
  },
  {
    question: 'What do I get out of it?',
    answer:
      'You help build Teamplay from early on, own real work, and grow with it as it scales.',
  },
];
